import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  INSURANCE_CONVENTION_RISKS,
  INSURANCE_CONVENTION_RISK_TYPES,
  INSURANCE_TYPES_QUERY
} from 'features/insuranceConventions/edit/queries';
import { Grid, Box, Icon, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button, FakeText, Typography } from '@totalsoft/rocket-ui';
import AddIcon from '@mui/icons-material/Add';
import { RisksCheckboxHeaderCell, RisksNumericHeaderCell } from '../DetailsStyles';
import { StyledActionHeaderCell, StyledHeaderCell, StyledGrayIcon, StyledTable } from 'components/common/Styles';
import InsuranceConventionRiskContainer from './InsuranceConventionRiskContainer';
import { ADD_UPDATE_INSURANCE_CONVENTION_RISK, DELETE_INSURANCE_CONVENTION_RISK } from 'features/insuranceConventions/edit/mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { INSURANCE_CONVENTION_RISK_DELETED, INSURANCE_CONVENTION_RISK_SAVED } from 'features/insuranceConventions/edit/subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { sortBy } from 'utils/functions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { addCachedDetail, removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';

const InsuranceConventionRisksContainer = ({ productId }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const confirm = useConfirmDialog();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const [risks, setRisks] = useState([]);

  const [inAdd, setInAdd] = useState(false);

  const isNewConvention = useMemo(() => !productId, [productId]);

  const { loading } = useQuery(INSURANCE_CONVENTION_RISKS, {
    skip: !productId,
    variables: { productId },
    onCompleted: data => {
      const newRisks = (data?.insuranceConventionRisks || []).map(x => ({ ...x, disabled: false, editing: false, isNew: false }));
      setRisks(newRisks);
    }
  });

  const { data: insuranceTypesData } = useQuery(INSURANCE_TYPES_QUERY);

  const { data: riskTypesData } = useQuery(INSURANCE_CONVENTION_RISK_TYPES);

  const insuranceTypes = useMemo(() => insuranceTypesData?.insuranceTypes || [], [insuranceTypesData]);

  const riskTypes = useMemo(() => riskTypesData?.insuranceConventionRiskTypes || [], [riskTypesData]);

  const handleAdd = useCallback(() => {
    setRisks(prev => [{ isNew: true, key: crypto.randomUUID(), productId }, ...prev]);
    setInAdd(true);
  }, [productId]);

  const handleCancelAdd = useCallback(() => {
    setRisks(prev => prev.filter((_, i) => i !== 0));
    setInAdd(false);
  }, []);

  const updateLocalRisk = useCallback(
    (insuranceTypeId, riskTypeId, property, value) => {
      setRisks(prev => {
        const modifyRisk = risk => {
          const newRisk = { ...risk };
          newRisk[property] = value;
          if (property === 'editing' && !value) newRisk.disabled = false;
          return newRisk;
        };
        const newRisks = prev.map(x =>
          x.productId === productId && x.insuranceTypeId === insuranceTypeId && x.riskTypeId === riskTypeId ? modifyRisk(x) : x
        );
        return newRisks;
      });
    },
    [productId]
  );

  // #region Mutate

  const [mutateRisk] = useMutation(ADD_UPDATE_INSURANCE_CONVENTION_RISK, {
    onError: ({ params }) => {
      updateLocalRisk(params.insuranceTypeId, params.riskTypeId, 'disabled', false);
    }
  });

  const handleMutateRisk = useCallback(
    risk => {
      const existingRisk = risks.find(x => x.insuranceTypeId === risk.initialInsuranceTypeId && x.riskTypeId === risk.initialRiskTypeId);
      if (risks.some(x => x !== existingRisk && x.insuranceTypeId === risk.insuranceTypeId && x.riskTypeId === risk.riskTypeId)) {
        addToast(t('InsuranceConventions.Details.Risks.AlreadyExists'), 'error');
        return;
      }

      updateLocalRisk(risk.initialInsuranceTypeId, risk.initialRiskTypeId, 'disabled', true);

      // eslint-disable-next-line no-unused-vars
      const { isNew, key, editing, disabled, insuranceTypeName, riskTypeName, ...sanitizedRisk } = risk;

      mutateRisk({
        variables: { input: sanitizedRisk },
        errorHandlingParams: { insuranceTypeId: risk.initialInsuranceTypeId, riskTypeId: risk.initialRiskTypeId }
      });
    },
    [mutateRisk, risks, addToast, t, updateLocalRisk]
  );

  // #endregion

  // #region Delete

  const [deleteRisk] = useMutation(DELETE_INSURANCE_CONVENTION_RISK, {
    onError: ({ params }) => {
      updateLocalRisk(params.insuranceTypeId, params.riskTypeId, 'disabled', false);
    }
  });

  const handleDeleteRisk = useCallback(
    (productId, insuranceTypeId, riskTypeId) => {
      confirm(
        () => {
          updateLocalRisk(insuranceTypeId, riskTypeId, 'disabled', true);
          deleteRisk({
            variables: { productId, insuranceTypeId, riskTypeId },
            errorHandlingParams: { insuranceTypeId, riskTypeId }
          });
        },
        {
          title: t('InsuranceConventions.Details.Risks.DeleteRisk'),
          content: t('InsuranceConventions.Details.Risks.AreYouSureDeleteRisk')
        }
      );
    },
    [deleteRisk, confirm, t, updateLocalRisk]
  );

  // #endregion

  useSubscription(INSURANCE_CONVENTION_RISK_SAVED, {
    onData: ({
      data: {
        data: {
          insuranceConventionRiskSaved: { isNew, productId, initialInsuranceTypeId, initialRiskTypeId, risk }
        }
      },
      client
    }) => {
      if (isNew) {
        handleCancelAdd();
        setRisks(prev => {
          const newRisks = [...prev, risk];
          sortBy(newRisks, ['productId', 'insuranceTypeId', 'riskTypeId']);
          return newRisks;
        });
      } else {
        setRisks(prev => {
          const newRisks = prev.map(x =>
            x.productId === productId && x.insuranceTypeId === initialInsuranceTypeId && x.riskTypeId === initialRiskTypeId
              ? { ...risk, editing: false, disabled: false }
              : x
          );
          sortBy(newRisks, ['productId', 'insuranceTypeId', 'riskTypeId']);
          return newRisks;
        });
      }
      addCachedDetail(
        client,
        { name: 'insuranceConventionRisks', value: INSURANCE_CONVENTION_RISKS },
        { productId },
        x => x.productId === risk.productId && x.insuranceTypeId === initialInsuranceTypeId && x.riskTypeId === initialRiskTypeId,
        risk,
        ['productId', 'insuranceTypeId', 'riskTypeId']
      );
      addToast(t('InsuranceConventions.Details.Risks.RiskSaved'), 'success');
    }
  });

  useSubscription(INSURANCE_CONVENTION_RISK_DELETED, {
    onData: ({
      data: {
        data: {
          insuranceConventionRiskDeleted: { productId, insuranceTypeId, riskTypeId }
        }
      },
      client
    }) => {
      setRisks(prev =>
        prev.filter(x => !(x.productId === productId && x.insuranceTypeId === insuranceTypeId && x.riskTypeId === riskTypeId))
      );
      removeCachedDetail(
        client,
        { name: 'insuranceConventionRisks', value: INSURANCE_CONVENTION_RISKS },
        { productId },
        x => x.productId === productId && x.insuranceTypeId === insuranceTypeId && x.riskTypeId === riskTypeId,
        ['productId', 'insuranceTypeId', 'riskTypeId']
      );
      addToast(t('InsuranceConventions.Details.Risks.RiskDeleted'), 'success');
    }
  });

  const getRiskKey = useCallback(risk => (risk.isNew ? risk.key : `${risk.insuranceTypeId}_${risk.riskTypeId}`), []);

  return (
    <>
      {isNewConvention ? (
        <Grid container flexDirection='column' gap={1} p={2}>
          <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
            <Icon fontSize='small' color='primary'>
              error
            </Icon>
            <Typography color='textPrimary'>{t('InsuranceConventions.Details.Warning')}</Typography>
          </Box>
        </Grid>
      ) : (
        <StyledTable minWidth='800px'>
          <TableHead>
            <TableRow>
              <StyledHeaderCell title={t('InsuranceConventions.Details.Risks.InsuranceType')}>
                {t('InsuranceConventions.Details.Risks.InsuranceType')}
              </StyledHeaderCell>
              <StyledHeaderCell title={t('InsuranceConventions.Details.Risks.Risk')}>
                {t('InsuranceConventions.Details.Risks.Risk')}
              </StyledHeaderCell>
              <RisksNumericHeaderCell title={t('InsuranceConventions.Details.Risks.RiskPercent')}>
                {t('InsuranceConventions.Details.Risks.RiskPercent')}
              </RisksNumericHeaderCell>
              <RisksNumericHeaderCell title={t('InsuranceConventions.Details.Risks.RiskAmount')}>
                {t('InsuranceConventions.Details.Risks.RiskAmount')}
              </RisksNumericHeaderCell>
              <RisksCheckboxHeaderCell title={t('InsuranceConventions.Details.Risks.Default')}>
                {t('InsuranceConventions.Details.Risks.Default')}
              </RisksCheckboxHeaderCell>
              <RisksCheckboxHeaderCell title={t('InsuranceConventions.Details.Risks.Mandatory')}>
                {t('InsuranceConventions.Details.Risks.Mandatory')}
              </RisksCheckboxHeaderCell>
              <RisksCheckboxHeaderCell title={t('InsuranceConventions.Details.Risks.ReadOnly')}>
                {t('InsuranceConventions.Details.Risks.ReadOnly')}
              </RisksCheckboxHeaderCell>
              <StyledActionHeaderCell align='right'>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  disabled={inAdd || isNewConvention || !canWrite}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('Add')}
                </Button>
              </StyledActionHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {!isNewConvention && loading && (
                <TableCell colSpan={8}>
                  <FakeText lines={5} />
                </TableCell>
              )}
              {!isNewConvention && !loading && risks.length === 0 && (
                <TableCell colSpan={8}>
                  <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
                    <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
                    <Typography color='textSecondary'>{t('InsuranceConventions.Details.AddValues')}</Typography>
                  </Box>
                </TableCell>
              )}
            </TableRow>
            {!isNewConvention &&
              !loading &&
              risks.length > 0 &&
              risks.map(risk => (
                <InsuranceConventionRiskContainer
                  key={getRiskKey(risk)}
                  risk={risk}
                  insuranceTypes={insuranceTypes}
                  riskTypes={riskTypes}
                  onUpdateLocalRisk={updateLocalRisk}
                  onMutateRisk={handleMutateRisk}
                  onDeleteRisk={handleDeleteRisk}
                  onCancelAdd={handleCancelAdd}
                />
              ))}
          </TableBody>
        </StyledTable>
      )}
    </>
  );
};

InsuranceConventionRisksContainer.propTypes = {
  productId: PropTypes.number
};

export default InsuranceConventionRisksContainer;
